import { useEffect, useRef, useState, ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { ClockIcon, BanknotesIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';

export type SpeedDialAction = 'NewTimesheet' | 'NewExpense';

interface SpeedDialItem {
  label: string;
  icon: ComponentType<{ className?: string }>;
  action: SpeedDialAction;
}

const ITEMS: readonly SpeedDialItem[] = [
  { label: 'New Timesheet', icon: ClockIcon, action: 'NewTimesheet' },
  { label: 'New Expense', icon: BanknotesIcon, action: 'NewExpense' },
];

interface MobileSpeedDialProps {
  onSelect: (_action: SpeedDialAction) => void;
}

/**
 * Floating action button, mobile only, that expands into New Timesheet / New
 * Expense shortcuts. Rendered into `document.body` so it escapes the scrolling
 * main region and stays pinned above the safe-area insets.
 */
export default function MobileSpeedDial({ onSelect }: MobileSpeedDialProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside tap, and on Escape — a dial that can only be dismissed
  // by hitting its own 56px button traps a keyboard user.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return createPortal(
    <div
      ref={ref}
      className="md:hidden fixed z-50"
      style={{
        right: 'max(env(safe-area-inset-right, 0px), 16px)',
        bottom: 'calc(max(env(safe-area-inset-bottom, 0px), 16px) + 68px)',
      }}
    >
      {isOpen && (
        <div className="flex flex-col items-end gap-3 mb-3">
          {ITEMS.map(item => (
            <button
              key={item.action}
              type="button"
              onClick={() => {
                onSelect(item.action);
                setIsOpen(false);
              }}
              className="flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              <span className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap shadow-md">
                {item.label}
              </span>
              <span className="flex items-center justify-center w-11 h-11 bg-blue-600 text-white rounded-full shadow-md">
                <item.icon className="w-5 h-5" />
              </span>
            </button>
          ))}
        </div>
      )}

      <Button variant="primary" onClick={() => setIsOpen(prev => !prev)} aria-label={isOpen ? 'Close menu' : 'Create new'} aria-expanded={isOpen} className="w-14 touch-manipulation">
        {isOpen ? <XMarkIcon className="w-7 h-7" /> : <PlusIcon className="w-7 h-7" />}
      </Button>
    </div>,
    document.body
  );
}
